import React, { useEffect, useState } from 'react'
import {
	Box,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'

import { AppDialog, AppDialogBody, AppDialogHeader, AppEmptyLine, AppNote, AppTableSkeleton } from '@components'
import { displayDay } from '@utils'
import { mbScopeSx } from 'Pages/MyBusiness/_shared/Ui'

/**
 * Drill-down dialog for Revenue: shows the partners (booking channels
 * by name) that fed the selected anchor — either a `channelType`
 * (Direct / B2B / OTA / External / Other / Unmapped) or a `sipp` code.
 *
 * Open it with:
 *   <PartnerDrillDialog anchor={{ channelType: 'OTA' }} filters={applied}
 *                       onClose={() => setAnchor(null)}/>
 *
 * If both anchors are present, the dialog narrows to "partners that
 * sold this type × this SIPP" in the active filter window.
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtEur = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR',
		minimumFractionDigits: 0, maximumFractionDigits: 0,
	}).format(n)

function buildQs(filters, anchor) {
	const qs = new URLSearchParams({
		from: filters.date.from,
		to:   filters.date.to,
	})
	if (filters.offices?.length)    qs.set('offices', filters.offices.join(','))
	if (filters.categories?.length) qs.set('sipps',   filters.categories.join(','))
	if (filters.duration && filters.duration !== 'total') qs.set('duration', filters.duration)
	if (anchor?.channelType) qs.set('channel_type', anchor.channelType)
	if (anchor?.sipp)        qs.set('sipp',         anchor.sipp)
	return qs.toString()
}

function describeAnchor(anchor) {
	const parts = []
	if (anchor?.channelType) parts.push(`Channel: ${anchor.channelType}`)
	if (anchor?.sipp)        parts.push(`Category: ${anchor.sipp}`)
	return parts.join(' · ') || 'All partners'
}

export default function PartnerDrillDialog({ anchor, filters, onClose }) {
	const open = !!anchor
	const [data, setData]       = useState(null)
	const [error, setError]     = useState(null)
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (!open) { setData(null); setError(null); return }
		setLoading(true); setError(null)
		fetch(`/api/my-business/revenue/by-partner?${buildQs(filters, anchor)}`,
		      { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [open, anchor?.channelType, anchor?.sipp,
	    filters?.date?.from, filters?.date?.to,
	    (filters?.offices || []).join(','),
	    (filters?.categories || []).join(','),
	    filters?.duration])

	return (
		<AppDialog open={open} onClose={onClose} maxWidth="md">
			<AppDialogHeader icon="handshake"
			                 title="Partners breakdown"
			                 subtitle={`${describeAnchor(anchor)} · ${displayDay(filters?.date?.from)} → ${displayDay(filters?.date?.to)}`}
			                 onClose={onClose}/>
			<AppDialogBody sx={{ ...mbScopeSx, pt: 1.5 }}>
				{error && <AppNote severity="error" sx={{ mb: 2 }}>{error}</AppNote>}

				{loading && <AppTableSkeleton rows={5} columns={7}/>}

				{data && !loading && data.rows.length === 0 && (
					<Box sx={{ py: 1, textAlign: 'center' }}>
						<AppEmptyLine>No partners matched in this window.</AppEmptyLine>
					</Box>
				)}

				{data && !loading && data.rows.length > 0 && (
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Partner</TableCell>
									<TableCell>Channel</TableCell>
									<TableCell align="right">Bookings</TableCell>
									<TableCell align="right">Revenue</TableCell>
									<TableCell align="right">Days</TableCell>
									<TableCell align="right">RPD</TableCell>
									<TableCell align="right">Share</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.rows.map((r, i) => (
									<TableRow key={i} hover>
										<TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
										<TableCell>
											<Chip size="small" label={r.channel_type}
											      sx={{ fontWeight: 600 }}/>
										</TableCell>
										<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
											{fmtNum(r.bookings)}
										</TableCell>
										<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
											{fmtEur(r.revenue)}
										</TableCell>
										<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
											{fmtNum(r.rental_days)}
										</TableCell>
										<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
											{fmtEur(r.rpd)}
										</TableCell>
										<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
											{r.share_pct.toFixed(1)}%
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</AppDialogBody>
		</AppDialog>
	)
}
